import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Customer } from "@/types";

function useCustomersClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

export function useCreateCustomer(): UseMutationResult<Customer, Error, { name: string; email: string }> {
  const getClient = useCustomersClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => getClient().post<Customer>("/customers", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("customers") }),
  });
}

export function useSuspendCustomer(): UseMutationResult<
  Customer,
  Error,
  { id: string; suspended: boolean }
> {
  const getClient = useCustomersClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, suspended }) =>
      getClient().post<Customer>(`/customers/${id}/suspend`, { suspended }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("customers") }),
  });
}
